import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Loader2, ShieldAlert } from "lucide-react";
import { ROLE_LABELS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

const ROLE_OPTS = [
  { value: "company_admin", label: "会社管理者" },
  { value: "property_manager", label: "不動産管理担当" },
  { value: "accountant", label: "経理担当" },
  { value: "viewer", label: "閲覧ユーザー" },
];
const ROLE_MAP = Object.fromEntries(Object.entries(ROLE_LABELS).map(([k, v]) => [k, { label: v, tone: k === "system_admin" ? "orange" : "blue" }]));

export default function SettingsUsers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "company_admin" || user?.role === "system_admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "viewer", password: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/settings/users"); setUsers(data); }
    catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [load, isAdmin]);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", role: "viewer", password: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, role: u.role, password: "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/settings/users/${editing.id}`, { name: form.name, role: form.role, password: form.password || undefined });
        toast.success("更新しました");
      } else {
        await api.post("/settings/users", form);
        toast.success("ユーザーを作成しました");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "保存に失敗しました"); }
    finally { setSaving(false); }
  };

  const del = async (u) => {
    try { await api.delete(`/settings/users/${u.id}`); toast.success("削除しました"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "削除に失敗しました"); }
  };

  if (!isAdmin) {
    return (
      <div className="animate-fade-in">
        <PageHeader title="ユーザー管理" />
        <div className="bg-white border border-border rounded-sm py-16 text-center text-muted-foreground">
          <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-gray-300" />この画面へのアクセス権限がありません
        </div>
      </div>
    );
  }

  const columns = [
    { key: "name", label: "氏名", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "email", label: "メール" },
    { key: "role", label: "権限", render: (r) => <StatusBadge value={r.role} map={ROLE_MAP} /> },
    { key: "active", label: "状態", render: (r) => (r.active === false ? "無効" : "有効") },
  ];

  return (
    <div className="animate-fade-in">
      <PageHeader title="ユーザー管理" subtitle="社内ユーザーと権限の管理"
        actions={<Button onClick={openNew} className="bg-brand hover:bg-brand-hover text-white" data-testid="add-user"><Plus className="w-4 h-4 mr-1.5" />ユーザーを追加</Button>} />
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
      ) : (
        <DataTable columns={columns} rows={users} canWrite testid="users"
          onEdit={openEdit} onDelete={(u) => del(u)} />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing ? "ユーザーを編集" : "ユーザーを追加"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">氏名 *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">メール *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="user-email" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">権限</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full h-10 px-3 rounded-sm border border-input bg-white text-sm" data-testid="user-role">
                {ROLE_OPTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">パスワード {editing && "（変更する場合のみ）"}{!editing && "*"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
            <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="user-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
